// src/components/CrystalsHeader.jsx

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { setLink, syncTelegramMeta, getPromoterLinkDisplay, getPromoterAvatarSrc, normalizeTelegramLink, isTelegramPromoterLink, TELEGRAM_LINK_ONLY_MESSAGE, TELEGRAM_NOT_FOUND_MESSAGE } from '../../../store/slices/promoTelegramSlice';
import { setBudget, setSelectedBlockchain, setFoundToken } from '../../../store/slices/budgetSlice';
import { setLink as setFormLink, syncWithBudget, syncWithCrystals, syncWithPromoTime, setParticipantsCount, setPrizePlacesCount, setMaxParticipantsCount } from '../../../store/slices/promoFormSlice';
import Lottie from 'lottie-react';
import swipeAnim from '../../../assets/lottie/swipe.json';
import getIcon from '../../../utils/Icon';
import CoinBadge from '../../coin_badge/CoinBadge';
import CrystalsApi from '../../../api/CrystalsApi';
import feeApi from '../../../api/feeApi';
import { selectSelectedDay, CRYPTO_CRYSTALS_DEFAULT_PROMO_DAY } from '../../../store/slices/promoTimeSlice';
import { useKeyboard } from '../../Keyboard';
import AccordionArrow from '../../icons/AccordionArrow';
import {
    getEffectiveParticipantsCount,
    getEffectiveWinnersCount,
    getPlayersKeyboardLabel,
    getWinnersKeyboardLabel,
    clampParticipantsCount,
    clampWinnersCount,
} from '../../../utils/participantLimits';
import { getCrystalRarityTier } from '../../../data/crystalRarityTiers';
import { PromoSnackbar, usePromoSnackbar } from './PromoSnackbar';
import { addRecentLink, getRecentLinks, PROMOTER_TG_LINK_RECENT_KEY } from '../../../utils/keyboardRecentLinks';

const IMAGE_BASE_URL = 'https://api.duotetra.quest/uploads/gems/';

const SUBSCRIPT_DIGITS = '₀₁₂₃₄₅₆₇₈₉';

const toSubscript = (value) =>
    String(value).split('').map((digit) => SUBSCRIPT_DIGITS[Number(digit)]).join('');

const formatLeadingZeroDecimal = (num) => {
    if (!(num > 0 && num < 1)) return null;

    const normalized = num.toFixed(20).replace(/(\.\d*?)0+$/, '$1');
    const match = normalized.match(/^0\.(0+)([1-9]\d*)$/);
    if (!match) return null;

    const zeroCount = match[1].length;
    if (zeroCount < 2) return null;

    const significant = match[2].slice(0, 2);
    return `0.0${toSubscript(zeroCount)}${significant}`;
};

const formatTokenName = (value) => {
    const str = String(value || '');
    if (str.length <= 6) return str;
    return `${str.slice(0, 5)}...`;
};

const formatThousands = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);

    if (Number.isInteger(num)) {
        return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }

    const normalized = num.toFixed(4).replace(/\.?0+$/, '');
    const [intPart, decPart] = normalized.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
};

const formatCompactBudget = (value) => {
    if (value == null || value === '') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return String(value);

    if (num >= 1_000_000_000_000) {
        return `${(num / 1_000_000_000_000).toFixed(2).replace(/\.?0+$/, '')} T`;
    }
    if (num >= 1_000_000_000) {
        return `${(num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')} B`;
    }
    if (num >= 1_000_000) {
        return `${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, '')} M`;
    }
    if (num >= 1000) {
        return `${(num / 1000).toFixed(2).replace(/\.?0+$/, '')} k`;
    }
    return formatThousands(num);
};

export default function CryptoAndCrystalHeader() {
    const dispatch = useDispatch();
    const { link, data, loading: tgLoading } = useSelector(s => s.promoTelegram);
    const { items: crystals, selectedId, loading: crystalsLoading } = useSelector(s => s.crystals);
    const { budget, foundToken, selectedBlockchain } = useSelector(s => s.budget);
    const selectedDay = useSelector(selectSelectedDay);
    const formLink = useSelector(s => s.promoForm.link);
    const selectedCategory = crystals.find(cat => cat.id === selectedId);
    const LINK_PLACEHOLDER = 'Promoter TG link';
    const LEGACY_LINK_PLACEHOLDER = 'Promoter link';
    const normalizePromoterLink = useCallback((value) => {
        const trimmed = (value || '').trim();
        if (!trimmed) return '';
        const lower = trimmed.toLowerCase();
        if (lower === LINK_PLACEHOLDER.toLowerCase() || lower === LEGACY_LINK_PLACEHOLDER.toLowerCase()) return '';
        return trimmed;
    }, []);

    const promoterLinkDisplay = useMemo(() => {
        if (!link || link === LINK_PLACEHOLDER || link === LEGACY_LINK_PLACEHOLDER) {
            return LINK_PLACEHOLDER;
        }
        return getPromoterLinkDisplay(link, data, LINK_PLACEHOLDER);
    }, [link, data]);

    const hasResolvedPromoterLink = promoterLinkDisplay !== LINK_PLACEHOLDER;

    // Кастомные значения участников/победителей из Redux
    const customParticipants = useSelector(s => s.promoForm.participantsCount);
    const customWinners = useSelector(s => s.promoForm.prizePlacesCount);
    const [showPromoterTooltip, setShowPromoterTooltip] = useState(false);
    const [showPrizePoolTooltip, setShowPrizePoolTooltip] = useState(false);
    const [activePresetModal, setActivePresetModal] = useState(null);
    const [playersTouched, setPlayersTouched] = useState(false);
    const [winnersTouched, setWinnersTouched] = useState(false);
    const [compactCategoryView, setCompactCategoryView] = useState(false);
    const { snackbar, snackbarAnimation, showSnackbar, closeSnackbar } = usePromoSnackbar();
    const promoterRef = useRef(null);
    const prizePoolRef = useRef(null);

    useEffect(() => {
        if (!showPromoterTooltip) return;

        const handleClickOutside = (event) => {
            if (promoterRef.current && !promoterRef.current.contains(event.target)) {
                setShowPromoterTooltip(false);
            }
        };

        document.addEventListener('pointerdown', handleClickOutside);
        return () => document.removeEventListener('pointerdown', handleClickOutside);
    }, [showPromoterTooltip]);

    useEffect(() => {
        if (!showPrizePoolTooltip) return;

        const handleClickOutside = (event) => {
            if (prizePoolRef.current && !prizePoolRef.current.contains(event.target)) {
                setShowPrizePoolTooltip(false);
            }
        };

        document.addEventListener('pointerdown', handleClickOutside);
        return () => document.removeEventListener('pointerdown', handleClickOutside);
    }, [showPrizePoolTooltip]);

    // Синхронизируем link из promoFormSlice с promoTelegramSlice при загрузке
    useEffect(() => {
        const normalizedFormLink = normalizePromoterLink(formLink);
        const normalizedCurrentLink = normalizePromoterLink(link);
        if (normalizedFormLink && normalizedFormLink !== normalizedCurrentLink) {
            dispatch(setLink(normalizedFormLink));
        }
    }, [formLink, link, dispatch, normalizePromoterLink]);

    // Синхронизируем данные с promoFormSlice
    useEffect(() => {
        dispatch(syncWithBudget({ budget, selectedBlockchain, foundToken }));
    }, [budget, selectedBlockchain, foundToken, dispatch]);

    useEffect(() => {
        if (selectedCategory) {
            dispatch(syncWithCrystals({ selectedId, crystals }));
        }
    }, [selectedId, crystals, dispatch]);

    useEffect(() => {
        if (selectedDay) {
            dispatch(syncWithPromoTime({ selectedDay }));
        }
    }, [selectedDay, dispatch]);

    // 1) Загружаем все GemType
    const [gemTypes, setGemTypes] = useState([]);
    
    useEffect(() => {
        CrystalsApi.getAllGems()
            .then(setGemTypes)
            .catch(err => console.error('Ошибка загрузки типов гемов:', err));
    }, []);

    // 2) Строим массив URL-ов frontImage по реверснутым position
    const [positionIcons, setPositionIcons] = useState([]);
    useEffect(() => {
        if (!selectedCategory || gemTypes.length === 0) {
            setPositionIcons([]);
            return;
        }
        const count = selectedCategory.number;
        const icons = Array.from({ length: count }, (_, i) => {
            const pos = count - i; // реверс: самая большая позиция first
            const gem = gemTypes.find(g => g.position === pos);
            return gem
                ? IMAGE_BASE_URL + gem.frontImage
                : getIcon('diamond');
        });
        setPositionIcons(icons);
    }, [selectedCategory, gemTypes]);

    // 3) Рассчитываем комиссию (fee) по minParticipantsCount
    const [fee, setFee] = useState(null);
    const [feeLoading, setFeeLoading] = useState(false);

    useEffect(() => {
        if (!selectedCategory) {
            setFee(null);

            return;
        }
        const count = getEffectiveParticipantsCount(customParticipants, selectedCategory);
        setFeeLoading(true);

        (async () => {
            try {
                const allFees = await feeApi.feeLevelsApi.getAll();

                if (!allFees?.length) {
                    setFee(null);
                    return;
                }
                // только для кристалов
                const filtered = allFees
                    .filter(f => !f.onlyGem)
                    .sort((a, b) => a.max - b.max);

                let resultFee = filtered[0]?.fee ?? 0;
                for (let i = 1; i < filtered.length; i++) {
                    if (count <= filtered[i].max) {
                        const prev = filtered[i - 1];
                        const curr = filtered[i];
                        const inc = (curr.fee - prev.fee) / (curr.max - prev.max);
                        resultFee = prev.fee + inc * (count - prev.max);
                        break;
                    }
                }
                if (count > filtered[filtered.length - 1].max) {
                    resultFee = filtered[filtered.length - 1].fee;
                }
                setFee(resultFee.toFixed(2));
            } catch (e) {
                console.error('Ошибка расчёта fee:', e);
                setFee(null);
            } finally {
                setFeeLoading(false);
            }
        })();
    }, [selectedCategory, customParticipants]);

    const { openKeyboard } = useKeyboard();

    // Telegram-инпут — через кастомную клавиатуру (без <input>)
    const handleOpenKeyboard = useCallback(() => {
        if (tgLoading) return;

        const username = window.Telegram?.WebApp?.initDataUnsafe?.user?.username;
        const selfLink = username ? `https://t.me/${username}` : '';
        const recent = getRecentLinks(PROMOTER_TG_LINK_RECENT_KEY);
        const suggestions = [selfLink, ...recent]
            .map((s) => String(s || '').trim())
            .filter(Boolean)
            .filter((link, idx, arr) => arr.indexOf(link) === idx);
        
        openKeyboard({
            type: 'qwerty',
            value: '',
            label: 'Promoter TG link',
            suggestions,
            suggestionsLabel: '',
            onChange: (finalValue) => {
                const normalizedValue = normalizePromoterLink(finalValue);
                if (!normalizedValue) return;
                if (!isTelegramPromoterLink(normalizedValue)) {
                    showSnackbar(TELEGRAM_LINK_ONLY_MESSAGE, 'error');
                    dispatch(setLink(''));
                    dispatch(setFormLink(''));
                    return;
                }
                const telegramLink = normalizeTelegramLink(normalizedValue);
                dispatch(setLink(telegramLink));
                dispatch(setFormLink(telegramLink));
                addRecentLink(PROMOTER_TG_LINK_RECENT_KEY, telegramLink);
            },
            onSubmit: async (finalValue) => {
                const normalizedValue = normalizePromoterLink(finalValue);
                if (!normalizedValue || !isTelegramPromoterLink(normalizedValue)) return;
                const telegramLink = normalizeTelegramLink(normalizedValue);
                addRecentLink(PROMOTER_TG_LINK_RECENT_KEY, telegramLink);
                try {
                    await dispatch(syncTelegramMeta(telegramLink)).unwrap();
                } catch (err) {
                    dispatch(setLink(''));
                    dispatch(setFormLink(''));
                    showSnackbar(
                        typeof err === 'string' ? err : TELEGRAM_NOT_FOUND_MESSAGE,
                        'error'
                    );
                }
            },
            liveUpdate: false,
        });
    }, [openKeyboard, dispatch, tgLoading, normalizePromoterLink, showSnackbar]);

    // Редактирование количества участников
    const handleEditParticipants = useCallback(() => {
        if (!selectedCategory) return;
        setPlayersTouched(true);
        openKeyboard({
            type: 'numeric',
            value: '',
            label: getPlayersKeyboardLabel(selectedCategory),
            onChange: (val) => {
                if (!val || val.trim() === '') return;
                const num = parseInt(val, 10);
                if (!isNaN(num) && num > 0) {
                    const clamped = clampParticipantsCount(num, selectedCategory);
                    dispatch(setParticipantsCount(clamped));
                    dispatch(setMaxParticipantsCount(clamped));
                    const currentWinners = getEffectiveWinnersCount(customWinners, selectedCategory);
                    const reclampedWinners = clampWinnersCount(currentWinners, clamped, selectedCategory);
                    if (reclampedWinners !== currentWinners) {
                        dispatch(setPrizePlacesCount(reclampedWinners));
                    }
                }
            },
            liveUpdate: false,
        });
    }, [openKeyboard, selectedCategory, customWinners, dispatch]);

    // Редактирование количества победителей
    const handleEditWinners = useCallback(() => {
        if (!selectedCategory) return;
        setWinnersTouched(true);
        const participants = getEffectiveParticipantsCount(customParticipants, selectedCategory);
        openKeyboard({
            type: 'numeric',
            value: '',
            label: getWinnersKeyboardLabel(selectedCategory, participants),
            onChange: (val) => {
                if (!val || val.trim() === '') return;
                const num = parseInt(val, 10);
                if (!isNaN(num) && num > 0) {
                    const clamped = clampWinnersCount(num, participants, selectedCategory);
                    dispatch(setPrizePlacesCount(clamped));
                }
            },
            liveUpdate: false,
        });
    }, [openKeyboard, selectedCategory, customParticipants, dispatch]);

    // Вспомогательные компоненты (CSS без изменений)
    const TimeSquares = ({ value }) => (
        <div className="w-[24px] h-[24px] text-[12px] font-roboto text-white flex items-center justify-center bg-white/15 rounded-[4px]">
            {value}
        </div>
    );
    const Time = () => (
        <div className="w-full h-[24px] flex justify-between">
            <span className="text-[14px] font-semibold text-[#D8D8D8]">Promo by</span>
            <div className="w-[150px] flex justify-end items-center gap-1">
                <TimeSquares value={selectedDay || CRYPTO_CRYSTALS_DEFAULT_PROMO_DAY} /><span className="text-[12px] font-roboto">D</span>
                <TimeSquares value={0} /><span className="text-[12px] font-roboto">H</span>
                <TimeSquares value={0} /><span className="text-[12px] font-roboto">M</span>
            </div>
        </div>
    );

    const EditedTime = () =>(
        <div className="whitespace-nowrap h-[24px] flex ">
            <div className=" flex justify-end items-center gap-1">
                <TimeSquares value={selectedDay || CRYPTO_CRYSTALS_DEFAULT_PROMO_DAY} /><span className="text-[12px] font-roboto">D</span>
            </div>
        </div>
    );


    const Crystal = ({ percentage, count, src }) => {
        const [imageLoaded, setImageLoaded] = useState(false);
        const [imageError, setImageError] = useState(false);

        return (
            <div className="w-[40px] h-[66px] flex flex-col items-center justify-center">
                <span className="text-[12px] font-roboto text-[#959595]">{percentage}%</span>
                <div className="w-[33px] h-[33px] flex items-center justify-center relative">
                    {!imageLoaded && !imageError && (
                        <div className="w-[33px] h-[33px] bg-white/10 rounded animate-pulse absolute inset-0" />
                    )}
                    <img
                        width={33}
                        height={33}
                        src={src}
                        className={`w-[33px] h-[33px] rounded transition-opacity duration-200 ${
                            imageLoaded ? 'opacity-100' : 'opacity-0'
                        }`}
                        onLoad={() => setImageLoaded(true)}
                        onError={() => setImageError(true)}
                        alt=""
                    />
                    {imageError && (
                        <div className="w-[33px] h-[33px] bg-white/10 rounded flex items-center justify-center absolute inset-0">
                            <span className="text-white text-[8px]">?</span>
                        </div>
                    )}
                </div>
                <span className="text-[12px] font-roboto text-white">{count}</span>
            </div>
        );
    };

    // Skeleton для строк таблицы
    const SkeletonRow = () => (
        <div className={`w-full grid grid-cols-6 gap-0 pl-2 border-t border-[#353232]`}>
            <div className="h-[27px] p-[7px] flex flex-row justify-center items-center shrink-0">
                <div className="w-[33px] h-[33px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[24px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[24px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[24px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[32px] bg-white/10 rounded animate-pulse" />
            </div>
            <div className="h-[27px] py-[7px] flex justify-center items-center">
                <div className="h-[12px] w-[32px] bg-white/10 rounded animate-pulse" />
            </div>
        </div>
    );

    const getPresetWinnersCount = (preset) => {
        const totalPlaces = getEffectiveWinnersCount(customWinners, selectedCategory);
        if (!totalPlaces || !preset?.win || !selectedCategory?.presets) return 0;

        const presets = selectedCategory.presets;
        const floorValues = presets.map((p, idx) => ({
            ...p,
            originalIndex: idx,
            floorValue: Math.floor((p.win / 100) * totalPlaces),
            decimalPart: ((p.win / 100) * totalPlaces) % 1,
        }));

        const sortedByDecimal = [...floorValues].sort((a, b) => b.decimalPart - a.decimalPart);
        const totalFloor = floorValues.reduce((sum, p) => sum + p.floorValue, 0);
        const remaining = totalPlaces - totalFloor;

        const result = [...floorValues];
        for (let i = 0; i < remaining; i++) {
            result[sortedByDecimal[i].originalIndex].floorValue++;
        }

        const currentIndex = presets.findIndex(p => p === preset);
        return result[currentIndex]?.floorValue ?? Math.floor((preset.win / 100) * totalPlaces);
    };

    const getPresetBudgetAmount = (preset) => {
        if (!preset?.budget_percent || !budget) return null;

        const allPresets = selectedCategory?.presets || [];
        const totalPercent = allPresets.reduce((sum, p) => sum + (p.budget_percent || 0), 0);
        if (totalPercent === 0) return null;

        const normalizedPercent = (preset.budget_percent / totalPercent) * 100;
        return (normalizedPercent / 100) * budget;
    };

    const getPresetRewardAmount = (preset) => {
        const winnersCount = getPresetWinnersCount(preset);
        const totalBudget = getPresetBudgetAmount(preset);
        if (!winnersCount || totalBudget == null) return null;
        return totalBudget / winnersCount;
    };

    const formatTableAmount = (amount) => {
        if (amount == null) return '';
        return amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2).replace(/\.?0+$/, '');
    };

    const formatBudgetAmount = (amount) => formatThousands(amount);

    const formatPresetBudgetCell = (value) => {
        if (value == null || value === '') return '';
        const num = Number(value);
        if (Number.isNaN(num)) return '';

        if (num >= 1_000_000_000_000) {
            return `${(num / 1_000_000_000_000).toFixed(2).replace(/\.?0+$/, '')} T`;
        }
        if (num >= 1_000_000_000) {
            return `${(num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')} B`;
        }
        if (num >= 1_000_000) {
            return `${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, '')} M`;
        }
        if (num >= 1000) {
            return `${(num / 1000).toFixed(2).replace(/\.?0+$/, '')} K`;
        }
        if (num >= 1) {
            return num.toFixed(2).replace(/\.?0+$/, '');
        }
        if (num > 0 && num < 1) {
            const subscriptFormatted = formatLeadingZeroDecimal(num);
            if (subscriptFormatted) return subscriptFormatted;
            return num.toFixed(2).replace(/\.?0+$/, '');
        }
        return String(num);
    };

    const formatRewardAmount = (amount) => {
        if (amount == null) return '';
        if (typeof amount !== 'number' || Number.isNaN(amount)) return String(amount);
        if (Number.isInteger(amount)) return String(amount);

        const subscriptFormatted = formatLeadingZeroDecimal(amount);
        if (subscriptFormatted) return subscriptFormatted;

        return amount.toFixed(8).replace(/\.?0+$/, '');
    };

    const formatRewardAmountFixed8 = (amount) => {
        if (amount == null) return '';
        if (typeof amount !== 'number' || Number.isNaN(amount)) return String(amount);
        if (Number.isInteger(amount)) return String(amount);
        return amount.toFixed(8);
    };

    const formatRewardModalAmount = (amount) => {
        if (amount == null) return '';
        if (typeof amount !== 'number' || Number.isNaN(amount)) return String(amount);

        if (amount > 0 && amount < 1) {
            const subscriptFormatted = formatLeadingZeroDecimal(amount);
            if (subscriptFormatted) return subscriptFormatted;
            return amount.toFixed(4).replace(/\.?0+$/, '');
        }

        const abs = Math.abs(amount);
        let decimals;
        if (abs >= 10_000) decimals = 0;
        else if (abs >= 1_000) decimals = 1;
        else if (abs >= 100) decimals = 2;
        else if (abs >= 10) decimals = 3;
        else if (abs >= 1) decimals = 4;
        else return String(amount);

        const formatted = abs.toFixed(decimals).replace(/\.?0+$/, '');
        return amount < 0 ? `-${formatted}` : formatted;
    };

    const formatRewardModalCoinBadgeCount = (amount) => {
        if (amount == null) return 0;
        if (typeof amount !== 'number' || Number.isNaN(amount)) return String(amount);

        if (amount > 0 && amount < 1) {
            return formatRewardAmountFixed8(amount);
        }

        const abs = Math.abs(amount);
        let decimals;
        if (abs >= 10_000) decimals = 0;
        else if (abs >= 1_000) decimals = 1;
        else if (abs >= 100) decimals = 2;
        else if (abs >= 10) decimals = 3;
        else if (abs >= 1) decimals = 4;
        else return String(amount);

        const formatted = decimals === 0
            ? String(Math.round(abs))
            : abs.toFixed(decimals).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

        const withSign = amount < 0 ? `-${formatted}` : formatted;
        if (abs < 1000) return withSign;

        const isNegative = withSign.startsWith('-');
        const unsigned = isNegative ? withSign.slice(1) : withSign;
        const [intPart, decPart] = unsigned.split('.');
        const spacedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        const spaced = decPart !== undefined ? `${spacedInt}.${decPart}` : spacedInt;
        return isNegative ? `-${spaced}` : spaced;
    };

    const formatMaxSixDigits = (value) => {
        if (value == null || value === '') return '';
        const raw = String(value);
        let digitCount = 0;
        let result = '';

        for (const ch of raw) {
            if (ch >= '0' && ch <= '9') {
                if (digitCount >= 6) break;
                digitCount += 1;
                result += ch;
            } else if (ch === '.' && !result.includes('.')) {
                result += ch;
            }
        }

        return result.replace(/\.$/, '');
    };

    const formatCompactCount = (value) => {
        if (value == null || value === '') return '';
        const num = Number(value);
        if (Number.isNaN(num)) return formatMaxSixDigits(value);

        if (num >= 1_000_000_000) {
            return `${(num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, '')}B`;
        }
        if (num >= 1_000_000) {
            return `${(num / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M`;
        }
        if (num >= 1000) {
            return `${(num / 1000).toFixed(2).replace(/\.?0+$/, '')}K`;
        }
        if (num > 0 && num < 1) {
            const subscriptFormatted = formatLeadingZeroDecimal(num);
            if (subscriptFormatted) return subscriptFormatted;
        }
        if (Number.isInteger(num)) {
            return String(num);
        }
        return formatMaxSixDigits(value);
    };

    const toRoman = (num) => {
        const romanNumerals = [
            { value: 10, symbol: 'X' },
            { value: 9, symbol: 'IX' },
            { value: 5, symbol: 'V' },
            { value: 4, symbol: 'IV' },
            { value: 1, symbol: 'I' },
        ];

        let result = '';
        for (const { value, symbol } of romanNumerals) {
            while (num >= value) {
                result += symbol;
                num -= value;
            }
        }
        return result;
    };

    const getRewardTier = (index, total) => getCrystalRarityTier(index, total);

    const PresetDetailModal = ({ modalData, onClose }) => {
        const [imageLoaded, setImageLoaded] = useState(false);
        const [imageError, setImageError] = useState(false);
        const presets = selectedCategory?.presets || [];
        const [slideIndex, setSlideIndex] = useState(0);
        const [direction, setDirection] = useState(0);
        const [showSwipeHint, setShowSwipeHint] = useState(false);

        useEffect(() => {
            if (!modalData) return;
            const list = selectedCategory?.presets || [];
            const idx = list.findIndex((p) => p === modalData.preset);
            setSlideIndex(idx >= 0 ? idx : modalData.iconId ?? 0);
            setDirection(0);
            setShowSwipeHint(list.length > 1);

            if (list.length <= 1) return undefined;

            const timer = setTimeout(() => setShowSwipeHint(false), 2500);
            return () => clearTimeout(timer);
        }, [modalData]);

        useEffect(() => {
            setImageLoaded(false);
            setImageError(false);
        }, [slideIndex]);

        if (!modalData || presets.length === 0) return null;

        const reversedOrder = Boolean(modalData.reversedOrder);
        const preset = presets[slideIndex];
        const iconId = slideIndex;
        const winnersCount = getPresetWinnersCount(preset);
        const rewardAmount = getPresetRewardAmount(preset);
        const tokenSymbol = foundToken?.symbol || foundToken?.name || 'ETH';

        const rewardTier = getRewardTier(slideIndex, presets.length);

        const goToSlide = (nextIndex) => {
            if (presets.length <= 1) return;

            let target = nextIndex;
            if (target < 0) target = presets.length - 1;
            if (target >= presets.length) target = 0;
            if (target === slideIndex) return;

            setShowSwipeHint(false);
            let dir;
            if (slideIndex === presets.length - 1 && target === 0) {
                dir = 1;
            } else if (slideIndex === 0 && target === presets.length - 1) {
                dir = -1;
            } else {
                dir = target > slideIndex ? 1 : -1;
            }
            if (reversedOrder) dir = -dir;
            setDirection(dir);
            setSlideIndex(target);
        };

        const goNext = () => goToSlide(reversedOrder ? slideIndex - 1 : slideIndex + 1);
        const goPrev = () => goToSlide(reversedOrder ? slideIndex + 1 : slideIndex - 1);

        const slideVariants = {
            enter: (dir) => ({
                x: dir === 0 ? 0 : dir > 0 ? 80 : -80,
                opacity: dir === 0 ? 1 : 0,
            }),
            center: { x: 0, opacity: 1 },
            exit: (dir) => ({
                x: dir > 0 ? -80 : 80,
                opacity: 0,
            }),
        };

        return (
            <motion.div
                className="fixed inset-0 z-50 flex justify-center px-4 pb-4 pt-[70%]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className=" absolute inset-0 bg-[#2C2C2CE5]/90 backdrop-blur-sm "
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                />
                <motion.div
                    className="relative z-10 w-full max-w-[300px] h-[300px] px-3 overflow-hidden "
                    initial={{ opacity: 0, scale: 0.92, y: 16 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.92, y: 16 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <AnimatePresence initial={false} custom={direction} mode="wait">
                        <motion.div
                            key={slideIndex}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.25, ease: 'easeOut' }}
                            drag="x"
                            dragConstraints={{ left: 0, right: 0 }}
                            dragElastic={0.15}
                            onDragEnd={(_, info) => {
                                if (info.offset.x < -30 || info.velocity.x < -30) {
                                    goNext();
                                } else if (info.offset.x > 30 || info.velocity.x > 30) {
                                    goPrev();
                                }
                            }}
                            className="relative flex flex-col items-center touch-pan-y cursor-grab active:cursor-grabbing"
                        >
                            <div className='bg-gradient-to-b from-[#353232] to-[#232323] absolute -bottom-2 left-0 right-0 m-auto bg-black w-[170px] h-[170px] rounded-[4px] z-0'></div>
                            <div className='flex flex-col items-center z-10'>
                                <span className="text-white text-[14px] font-semibold">
                                    {toRoman(slideIndex + 1)}
                                </span>
                                <div className="relative w-[100px] h-[100px]">
                                    {!imageLoaded && !imageError && (
                                        <div className="w-[100px] h-[100px] bg-black/10 rounded animate-pulse absolute inset-0" />
                                    )}
                                    <img
                                        width={100}
                                        height={100}
                                        src={positionIcons[iconId] || ''}
                                        className={`w-[100px] h-[100px] rounded object-contain transition-opacity duration-200 ${
                                            imageLoaded ? 'opacity-100' : 'opacity-0'
                                        }`}
                                        onLoad={() => setImageLoaded(true)}
                                        onError={() => setImageError(true)}
                                        alt=""
                                        draggable={false}
                                    />
                                    {imageError && (
                                        <div className="w-[120px] h-[120px] bg-black/10 rounded flex items-center justify-center absolute inset-0">
                                            <span className="text-white text-[12px]">?</span>
                                        </div>
                                    )}
                                </div>
                                <span
                                    className="text-[16px] font-medium text-white mb-2"
                                >
                                    {rewardTier.name} reward
                                </span>
                                <CoinBadge
                                    token={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : undefined}
                                    icon={foundToken ? undefined : 'ETH'}
                                    count={formatRewardModalCoinBadgeCount(rewardAmount)}
                                    countOnly
                                    isCenter
                                    fixedWidth={150}
                                    customColorText={"#DEC315"}
                                    fullCount
                                />
                                <div className="flex items-center gap-1 my-2">
                                    <img
                                        width={24}
                                        height={24}
                                        src={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : getIcon('ETH')}
                                        alt={tokenSymbol}
                                        className={`flex-shrink-0 object-contain ${foundToken ? 'rounded-full' : ''}`}
                                        draggable={false}
                                    />
                                    <span className="text-white text-[12px] font-semibold truncate max-w-[120px]">
                                        {tokenSymbol}
                                    </span>
                                </div>
                                <span className="text-white text-[12px]">
                                    {preset?.win ?? 0}% chance
                                </span>
                            </div>   
                        </motion.div>
                    </AnimatePresence>

                    <AnimatePresence>
                        {showSwipeHint && presets.length > 1 && (
                            <motion.div
                                className="flex justify-center mt-6 pointer-events-none"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.3 }}
                            >
                                <Lottie
                                    animationData={swipeAnim}
                                    loop
                                    style={{ width: 72, height: 72 }}
                                />
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            </motion.div>
        );
    };

    const CategoriesItem = ({ pos, count, img, winners, onOpen }) => (
        <div
            className="flex flex-col items-center gap-[1px] flex-shrink-0 relative cursor-pointer"
            style={{ minWidth: '42px', maxWidth: '65px' }}
            onClick={(e) => {
                e.stopPropagation();
                onOpen?.();
            }}
        >
            <span className="text-[#959595] text-[9px]">{toRoman(pos)}</span>
            <span className="text-[#DEC315] text-[11px] font-semibold">
                {formatCompactCount(count)}
            </span>
            <img
                width={34}
                height={34}
                src={img}
                alt={`gem-${pos}`}
                style={{ width: '34px', height: '34px', objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
            />
            {winners > 0 && (
                <span className="text-white text-[11px] font-semibold">{winners}</span>
            )}
        </div>
    );

    const CompactCategoryView = () => {
        const presets = selectedCategory?.presets || [];

        if (crystalsLoading) {
            return (
                <div className="flex justify-center py-4">
                    <span className="text-[12px] text-[#959595]">Загрузка...</span>
                </div>
            );
        }

        if (!selectedCategory || presets.length === 0) {
            return (
                <div className="flex justify-center items-center py-4">
                    <span className="text-[12px] text-[#959595]">Выберите категорию</span>
                </div>
            );
        }

        const gapClass = presets.length === 5 ? 'gap-4' : presets.length === 3 ? 'gap-7' : 'gap-[1px]';

        return (
            <div className="w-full flex flex-col items-center mt-[8px] mb-2">
                <div
                    className={`w-full flex flex-row items-center justify-center px-4 overflow-x-auto hide-scrollbar ${gapClass}`}
                    style={{ flexWrap: 'nowrap' }}
                >
                    {[...presets].reverse().map((preset, reverseIndex) => {
                        const originalIndex = presets.length - 1 - reverseIndex;
                        const positionNumber = originalIndex + 1;
                        const iconId = preset.iconId ?? originalIndex;
                        const rewardAmount = getPresetRewardAmount(preset);
                        const winners = getPresetWinnersCount(preset);
                        const tokenCount = rewardAmount == null ? '' : rewardAmount;

                        return (
                            <div key={originalIndex}>
                                <CategoriesItem
                                    pos={positionNumber}
                                    count={tokenCount}
                                    winners={winners}
                                    img={positionIcons[iconId] || ''}
                                    onOpen={() => setActivePresetModal({
                                        preset,
                                        iconId,
                                        reversedOrder: true,
                                    })}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

const TableItem = ({iconId, preset, onOpen})=>{
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    
    if (!selectedCategory || !preset) return null;
    
    return(
        <div
            className="w-full flex items-start justify-between gap-0 border-t border-[#353232] cursor-pointer active:opacity-80"
            onClick={() => onOpen({ preset, iconId })}
        >
            <div className="w-[23%] h-[33px] p-[7px] flex flex-row justify-center items-center shrink-0 relative">
                {/* Skeleton пока изображение загружается */}
                {!imageLoaded && !imageError && (
                    <div className="w-[33px] h-[33px] bg-white/10 rounded animate-pulse absolute inset-0" />
                )}
                <img 
                    width={33} 
                    height={33} 
                    src={positionIcons[iconId] || ''} 
                    className={`w-[33px] h-[33px] rounded transition-opacity duration-200 ${
                        imageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    onLoad={() => setImageLoaded(true)}
                    onError={() => setImageError(true)}
                    alt=""
                />
                {imageError && (
                    <div className="w-[33px] h-[33px] bg-white/10 rounded flex items-center justify-center absolute inset-0">
                        <span className="text-white text-[8px]">?</span>
                    </div>
                )}
            </div>
            {/* <div className="h-[27px] py-[7px] flex justify-center items-center">
                <span className={'text-[11px] text-[#959595]'}>{preset?.win || ''}</span>
            </div> */}
            <div className="w-[22%] h-[33px] py-[7px] flex justify-center items-center">
                <span className={'text-[13px] font-semibold text-white'}>
                    {getPresetWinnersCount(preset) || ''}
                </span>
            </div>
            {/* <div className="h-[27px] py-[7px] flex justify-center items-center">
                <span className={'text-[11px] text-[#959595]'}>{preset?.budget_percent || ''}</span>
            </div> */}
            <div className="w-[22%] h-[33px] py-[7px] flex justify-center items-center">
                <span className={'text-[13px] font-semibold text-[#DEC315]'}>
                    {formatPresetBudgetCell(getPresetBudgetAmount(preset))}
                </span>
            </div>
            <div className="w-[33%] h-[33px] py-[7px] flex pl-2 items-start bg-[#1A1704]">
                <span className="text-[13px] font-semibold text-white flex items-center gap-1">
                    <img
                        src={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : getIcon('ETH')}
                        className={`icon-img flex-shrink-0 ${foundToken ? 'rounded-full' : ''}`}
                        alt="token"
                        style={{
                            width: 16,
                            height: 16
                        }}
                    />

                    <span className='w-full flex items-center pl-1 text-[#DEC315]'>
                        {(() => {
                            const rewardAmount = getPresetRewardAmount(preset);
                            if (rewardAmount == null) return '';
                            if (typeof rewardAmount !== 'number' || Number.isNaN(rewardAmount)) {
                                return String(rewardAmount);
                            }
                            if (rewardAmount > 0 && rewardAmount < 1) {
                                return formatRewardAmount(rewardAmount);
                            }

                            const abs = Math.abs(rewardAmount);
                            let decimals = 4;

                            if (abs >= 10_000) decimals = 0;
                            else if (abs >= 1_000) decimals = 1;
                            else if (abs >= 100) decimals = 2;
                            else if (abs >= 10) decimals = 3;

                            const formatted = decimals === 0
                                ? String(Math.round(abs))
                                : abs.toFixed(decimals).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
                            return rewardAmount < 0 ? `-${formatted}` : formatted;
                        })()}
                    </span>

                    </span>
            </div>
        </div>
    )
}
    const CrystalTable = ()=>{


        return(
            <div className={'flex flex-col'}>
                 <div className={'w-full flex flex-row  '}>
                     <div className="w-[23%] h-[20px] flex justify-center items-center">
                         <span className={'text-[11px] font-[400] text-[#D8D8D8] '}>category</span>
                     </div>
                     {/* <div className="w-[16.67%] h-[20px] flex justify-center items-center">
                         <span className={'text-[10px] text-[#D8D8D8]'}>win %</span>
                     </div> */}
                     <div className="w-[22%] h-[20px] flex justify-center items-center">
                         <span className={'text-[11px] font-[400] text-[#D8D8D8] '}>winners</span>
                     </div>
                     {/* <div className="w-[16.67%] h-[20px] flex justify-center items-center">
                         <span className={'text-[10px] text-[#D8D8D8]'}>budget %</span>
                     </div> */}
                     <div className="w-[22%] h-[20px] flex justify-center items-center">
                         <span className={'text-[11px] font-[400] text-[#D8D8D8] '}>prize pool</span>
                     </div>
                     <div className="w-[33%] h-[20px] flex justify-center items-center">
                         <span className={'text-[11px] font-[800] text-white '}>reward</span>
                     </div>
                </div>
                {crystalsLoading && (
                    Array.from({ length: 7 }).map((_, i) => (
                        <SkeletonRow key={`sk_${i}`} />
                    ))
                )}
                {!crystalsLoading && (!selectedCategory || !selectedCategory.presets) && (
                    <div className="flex justify-center items-center py-4">
                        <span className="text-[12px] text-[#959595]">Выберите категорию</span>
                    </div>
                )}
                {!crystalsLoading && selectedCategory && selectedCategory.presets && (
                    selectedCategory.presets.map((preset, i) => (
                        <TableItem
                            key={i}
                            iconId={preset.iconId ?? i}
                            preset={preset}
                            onOpen={() => setActivePresetModal({
                                preset,
                                iconId: preset.iconId ?? i,
                                reversedOrder: true,
                            })}
                        />
                    ))
                )}
                {!crystalsLoading && selectedCategory && selectedCategory.presets && (
                    <div className={`w-full flex items-start justify-between gap-0 border-t border-[#353232]`}>
                        <div className="w-[23%] h-[33px] p-[7px] flex flex-row justify-center items-center shrink-0 relative">
                            <span className={'text-[13px] font-semibold text-white'}>Total</span>
                        </div>
                        <div className="w-[22%] h-[33px] p-[7px] flex flex-row justify-center items-center  ">
                            <span className={'text-[13px] font-semibold text-white'}>
                                {formatThousands(getEffectiveWinnersCount(customWinners, selectedCategory) || '')}
                            </span>
                        </div>
                        <div className="w-[22%] h-[33px] p-[7px] flex flex-row justify-center items-center  ">
                            <span className={'text-[13px] font-semibold text-[#DEC315]'}>
                                {budget ? formatCompactBudget(budget) : ''}
                            </span>
                        </div>
                        <div className="w-[33%] h-[33px] py-[7px] flex flex-row pl-2 gap-1 items-center bg-[#1A1704]">
                            <img
                                src={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : getIcon('ETH')}
                                className={`icon-img flex-shrink-0 ${foundToken ? 'rounded-full' : ''}`}
                                alt="token"
                                style={{
                                    width: 16,
                                    height: 16
                                }}
                            />
                            <span className="flex items-center pl-1 text-[13px] font-semibold text-white min-w-0">
                                <span className="text-[#D8D8D8] text-[12px] font-semi font-sans  min-w-0 max-w-[60px] xs:max-w-[40px]">
                                    {(() => {
                                        const tokenName = String(foundToken?.symbol || foundToken?.name || 'ETH');
                                        if (tokenName.length <= 10) return tokenName;
                                        return `${tokenName.slice(0, 10)}...`;
                                    })()}
                                </span>
                            </span>
                        </div>
                    </div>
                )}
            </div>
        )
    }

    const presetModal = (
        <AnimatePresence>
            {activePresetModal && (
                <PresetDetailModal
                    modalData={activePresetModal}
                    onClose={() => setActivePresetModal(null)}
                />
            )}
        </AnimatePresence>
    );

    // Линия кристаллов
    const CrystalsLine = () => {
        if (!selectedCategory) return null;
        return (
            <div className="w-full border-t border-b border-white/20 mt-1 h-[100px] p-[16px] flex items-center justify-center gap-[8.5px]">
                {selectedCategory.presets.map((preset, i) => (
                    <Crystal
                        key={i}
                        percentage={preset.budget_percent}
                        count={preset.win}
                        src={positionIcons[i]}
                    />
                ))}
            </div>
        );
    };

    const Promoter = () => (
        <div className="w-[45px] h-[45px] flex items-center gap-[15px]">
            <div className="w-[45px] h-[45px] rounded-full relative">
                <img
                    width={45} height={45}
                    src={getPromoterAvatarSrc(link, data)}
                    onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = getPromoterAvatarSrc(link || data?.link, { ...data, logo: null, logoPath: null });
                    }}
                    className="border border-[#3B3836] rounded-full object-cover w-[45px] h-[45px]"
                />
                <img
                    width={14.7} height={14.7}
                    src={getIcon('octo')}
                    className="absolute bottom-0 right-0 rounded-full"
                />
            </div>
        </div>
    );
    const ParticipantsValueRect = ({ title, count, percentage, onClick, isHideTitle, fixedWidth, showDot, dotTouched }) => {
        const resolvedWidth = fixedWidth != null
            ? (typeof fixedWidth === 'number' ? `${fixedWidth}px` : fixedWidth)
            : undefined;

        return (
        <div
            className={`relative flex flex-col items-center justify-center gap-[3px] ${onClick ? 'cursor-pointer' : ''} ${!resolvedWidth ? 'w-[70px]' : ''}`}
            style={resolvedWidth ? { width: resolvedWidth, minWidth: resolvedWidth, maxWidth: resolvedWidth } : undefined}
            onClick={onClick}
        >
            {showDot && !dotTouched && (
                <div
                    className="absolute top-2 left-1 w-1.5 h-1.5 rounded-full bg-[#FB2C36] shadow-[0_0_6px_#FB2C36]"
                />
            )}
            {
                isHideTitle ? 
                        `` :
                        <span className="text-[#D8D8D8] text-[11px] font-[400] text-[#D8D8D8]">{title}</span>
            }
            
            <div className={`w-full h-[24px] flex items-center justify-center font-roboto font-semibold text-[13px] rounded-[40px] border
                ${title === 'winners' ? 'border-[#DEC315]' : 'border-white/15'}
                ${!percentage ? 'bg-[#D3D3D3] text-black' : 'bg-white/20 text-white'}
                ${onClick ? 'active:scale-95 transition-transform' : ''}`}>
                {typeof count === 'number' ? formatThousands(count) : count}
            </div>
            {
                isHideTitle ? 
                `` 
                    :
                <span className="text-[11px] font-[300] text-[#959595] font-roboto">
                    {percentage ? `${percentage}%` : '\u00A0'}
                </span>
            }
            
        </div>
        );
    };

    // Обновлённый Participants (с редактированием)
    const Participants = () => {
        if (!selectedCategory) return null;

        const participantsCount = getEffectiveParticipantsCount(customParticipants, selectedCategory);
        const winnersCount      = getEffectiveWinnersCount(customWinners, selectedCategory);
        const nonWinnersCount   = participantsCount - winnersCount;
        const participantsPct   = 100;
        const winnersPct        = participantsCount > 0 ? Math.round(winnersCount / participantsCount * 100) : 0;
        const nonWinnersPct     = participantsCount > 0 ? Math.round(nonWinnersCount / participantsCount * 100) : 0;

        let costDisplay;
        if (feeLoading) {
            costDisplay = '...';
        } else if (fee != null) {
            costDisplay = `$${fee}`;
        } else {
            costDisplay = '—';
        }

        return (
            <div className=" w-full px-[13px] py-[6px] flex items-center justify-between gap-[9px] border border-[#353232] rounded-[5.5px]">
                <ParticipantsValueRect
                    title="players"
                    count={participantsCount}
                    percentage={participantsPct}
                    onClick={handleEditParticipants}
                    fixedWidth={80}
                    showDot
                    dotTouched={playersTouched}
                />
                <ParticipantsValueRect
                    title="winners"
                    count={winnersCount}
                    percentage={winnersPct}
                    onClick={handleEditWinners}
                    fixedWidth={80}
                    showDot
                    dotTouched={winnersTouched}
                />
                {/* <ParticipantsValueRect
                    title="non-winner"
                    count={nonWinnersCount}
                    percentage={nonWinnersPct}
                    fixedWidth={80}
                /> */}
                <div className='flex flex-col items-center justify-start gap-[3px] h-[62px]'>
                    <span className='text-[11px] font-[400] text-white'>non-winner</span>
                    <div className='w-[80px] h-[24px]  bg-black rounded-full  flex items-center justify-center flex-shrink-0 border border-[#353232] text-white'>
                        <span className='text-[13px] text-[#D8D8D8]'>{formatThousands(nonWinnersCount)}</span>
                    </div>
                    {
                        nonWinnersPct >1 && (
                            <span className="text-[11px] font-[300] text-[#959595] font-roboto">{nonWinnersPct}%</span>
                        )
                    }
                </div>
            </div>
        );
    };

    let costDisplay;
        if (feeLoading) {
            costDisplay = '...';
        } else if (fee != null) {
            costDisplay = `$${fee}`;
        } else {
            costDisplay = '—';
        }

    // Итоговый JSX
    return (
        <>
        <div className='w-full h-fit flex flex-col gap-[8px]'>
            <div className="w-full h-fit flex flex-col rounded-[8px] border border-white/20 overflow-hidden">
                <div className="px-[12px] py-[4.5px] w-full flex flex-col items-center gap-[5px]">
                    <div className='w-full flex items-center justify-between pt-2 '>
                        <div></div>
                        <span className="text-[14px] font-semibold text-[#D8D8D8]">Promo by</span>
                        <motion.button
                            type="button"
                            onClick={() => setCompactCategoryView(v => !v)}
                            className="cursor-pointer -translate-x-1"
                            animate={{ rotate: compactCategoryView ? 180 : 0 }}
                            transition={{ duration: 0.3, ease: 'easeInOut' }}
                        >
                            <AccordionArrow />
                        </motion.button>
                    </div>
                    
                    
                    <div className="w-full flex items-center justify-between gap-[12px]">
                        <div className='relative' ref={promoterRef}>
                            <div
                                className="cursor-pointer"
                                onClick={() => setShowPromoterTooltip(v => !v)}
                            >
                                <Promoter />
                            </div>

                            <AnimatePresence>
                                {showPromoterTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                                        transition={{ duration: 0.25, ease: 'easeOut' }}
                                        className={`min-w-[280px] ${data?.name? "h-28" : "h-20"} absolute top-[calc(100%+10px)] -left-2 z-10 bg-[#2373BE] rounded-[10px]`}
                                    >
                                        <div className='w-full h-full relative'>
                                            <div className='w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[7px] left-[22px]' />
                                            <div className='w-full h-full flex flex-col items-start p-3 gap-1'>
                                                <span className="text-white text-[14px] font-semibold">Promo Creator Name</span>
                                                {
                                                    data?.name && (
                                                        <span className="text-white text-[12px] font-semibold">{data?.name}</span>
                                                    )
                                                }
                                                <span className="text-white/80 text-[13px] truncate max-w-full">Telegram link only: channel / group / bot / account</span>
                                                {
                                                    data?.name && (
                                                        <span className="text-white/80 text-[11px] truncate max-w-full">{link }</span>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        
                        
                        <div className="flex flex-col gap-[4px] items-start overflow-hidden w-full">
                            <div 
                                className={`w-full h-[25px] rounded-[4.5px] bg-white/15 flex items-center justify-center relative gap-[6px] py-[3px] pl-3 pr-7 overflow-hidden cursor-pointer ${tgLoading ? 'opacity-50' : ''}`}
                                onClick={handleOpenKeyboard}
                            >
                                {!hasResolvedPromoterLink && (
                                    <div className="absolute left-[8px] top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#FB2C36] shadow-[0_0_6px_#FB2C36] flex-shrink-0" />
                                )}
                                <img
                                    width={12} height={12}
                                    src={getIcon('link')}
                                    className=" flex-shrink-0 absolute right-[9.5px]"
                                />
                                <span className={`${hasResolvedPromoterLink ? 'text-white/90 text-[14px]' : 'text-[#959595] text-[14px]'} truncate flex-1 text-center`}>
                                    {promoterLinkDisplay}
                                </span>
                            </div>
                        </div>
                        <EditedTime/>                    
                    </div>

                    <div className='w-full flex justify-between items-center '>

                        <div className='w-[50px] h-[24px] border-[2px] border-[#353232] rounded-[6px] flex flex-row items-center justify-center'>
                            <span className='text-[12px] text-[#D8D8D8]'>{(selectedCategory?.name ?? '')}</span>
                        </div>
                        
                        <div className='relative' ref={prizePoolRef}>
                            <div
                                className="cursor-pointer"
                                onClick={() => setShowPrizePoolTooltip(v => !v)}
                            >
                                <CoinBadge 
                                    token={foundToken ? `https://api.duotetra.quest/${foundToken.logoPath}` : undefined}
                                    icon={foundToken ? undefined : 'ETH'} 
                                    count={budget}
                                    classicCount
                                    name={foundToken?.symbol || foundToken?.name || 'ETH'}
                                    iconHeight={18} 
                                    iconWidth={18}
                                    isHeader={true}
                                    selectedBlockchain={selectedBlockchain || 'ETH'}
                                    fixedWidth={160}
                                    customTextClass={"translate-x-0.5"}
                                />
                            </div>

                            <AnimatePresence>
                                {showPrizePoolTooltip && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }} 
                                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                                        transition={{ duration: 0.25, ease: 'easeOut' }}
                                        className='absolute top-[calc(100%+10px)] -left-7  min-w-[220px] min-h-20 bg-[#2373BE] z-10 rounded-[10px]'
                                    >
                                        <div className='w-full h-full flex flex-col  items-center relative'>
                                            <div className='w-[12px] h-[7px] bg-[#2373BE] [clip-path:polygon(50%_0%,0%_100%,100%_100%)] absolute -top-[6.5px] left-0 right-0 m-auto' />
                                            <div className='w-full flex flex-col gap-1 p-3 text-center'>
                                                <span className='text-white text-[12px] font-semibold'>Giveaway Prize Pool</span>
                                                <span className='text-white/80 text-[12px]'>shared between all winners</span>
                                                <div className='w-full flex items-center justify-center'>
                                                    <span className='text-white text-[11px]'>
                                                        {formatBudgetAmount(budget)}{' '}
                                                        {foundToken?.symbol || foundToken?.name || 'ETH'}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        
                        
                        <ParticipantsValueRect
                            title="cost"
                            count={costDisplay}
                            isHideTitle={true}
                        />
            

                    </div>

                    
                </div>

                {compactCategoryView ? <CompactCategoryView /> : <CrystalTable />}

            </div>

            <Participants />

        </div>
        {presetModal}
        <PromoSnackbar
            message={snackbar.message}
            type={snackbar.type}
            isVisible={snackbar.isVisible}
            isAnimated={snackbarAnimation}
            onClose={closeSnackbar}
        />
        </>
    );
}
